/**
 * PageHeader — cabeçalho padrão de página com badge colorido + título + subtítulo opcional.
 *
 * Uso:
 *   <PageHeader
 *     badge={{ label: "PLATFORM", color: "rose" }}
 *     title="Plataforma"
 *     subtitle="Gestão multi-tenant — empresas, países e auditoria global."
 *   />
 *
 * Cores do badge (mapeadas pra paletas Tailwind):
 *   rose    → /platform
 *   indigo  → /admin
 *   purple  → /agents
 *   amber   → /comunicados
 *   sky     → genérico
 *   emerald → sucesso/ativo
 */
import type { ReactNode } from "react";

export type BadgeColor = "rose" | "indigo" | "purple" | "amber" | "sky" | "emerald" | "slate";

const BADGE_STYLES: Record<BadgeColor, string> = {
  rose:    "bg-rose-100 text-rose-700 dark:bg-rose-950/50 dark:text-rose-300",
  indigo:  "bg-indigo-100 text-indigo-700 dark:bg-indigo-950/50 dark:text-indigo-300",
  purple:  "bg-purple-100 text-purple-700 dark:bg-purple-950/50 dark:text-purple-300",
  amber:   "bg-amber-100 text-amber-700 dark:bg-amber-950/50 dark:text-amber-300",
  sky:     "bg-sky-100 text-sky-700 dark:bg-sky-950/50 dark:text-sky-300",
  emerald: "bg-emerald-100 text-emerald-700 dark:bg-emerald-950/50 dark:text-emerald-300",
  slate:   "bg-app-soft text-app-2",
};

interface PageHeaderProps {
  badge?:    { label: string; color?: BadgeColor };
  /** Alternativa ao badge: texto small uppercase acima do título (ex: "PERFIL") */
  eyebrow?:  string;
  title:     string;
  subtitle?: ReactNode;
  /** Slot opcional à direita do header (ex: botão "Novo") */
  actions?:  ReactNode;
}

export function PageHeader({ badge, eyebrow, title, subtitle, actions }: PageHeaderProps) {
  return (
    <div className="mb-6">
      <div className="flex items-start justify-between gap-4">
        <div className="min-w-0 flex-1">
          {eyebrow && (
            <p className="text-xs font-semibold text-app-3 uppercase tracking-widest mb-1">
              {eyebrow}
            </p>
          )}
          <div className="flex items-center gap-2 mb-1">
            {badge && (
              <span className={`px-2.5 py-1 rounded-lg text-xs font-semibold ${BADGE_STYLES[badge.color ?? "slate"]}`}>
                {badge.label}
              </span>
            )}
            <h1 className="text-2xl font-bold text-app-1 tracking-tight truncate">
              {title}
            </h1>
          </div>
          {subtitle && (
            <p className="text-sm text-app-3 mt-1">
              {subtitle}
            </p>
          )}
        </div>
        {actions && (
          <div className="flex items-center gap-2 shrink-0">
            {actions}
          </div>
        )}
      </div>
    </div>
  );
}
